import React, { useEffect, useState } from "react";
import { CheckCircle2, Truck, Flame } from "lucide-react";

const FREE_SHIPPING_THRESHOLD = 0; // free for everyone — used for visual celebration
const BASE_STOCK = 23; // synced with backend /stats/live

function getStockCountdownEnd() {
  const stored = parseInt(localStorage.getItem("dp_stock_end") || "0", 10);
  const now = Date.now();
  if (!stored || stored < now) {
    const end = now + 4 * 60 * 60 * 1000; // 4h flash window
    localStorage.setItem("dp_stock_end", String(end));
    return end;
  }
  return stored;
}

export default function ShippingProgressBar({ qty = 1, price = 0 }) {
  const subtotal = price;
  const reached = subtotal >= FREE_SHIPPING_THRESHOLD;

  const [end, setEnd] = useState(getStockCountdownEnd);
  const [remaining, setRemaining] = useState(end - Date.now());

  useEffect(() => {
    const t = setInterval(() => {
      const r = end - Date.now();
      if (r <= 0) {
        const newEnd = Date.now() + 4 * 60 * 60 * 1000;
        localStorage.setItem("dp_stock_end", String(newEnd));
        setEnd(newEnd);
      } else {
        setRemaining(r);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [end]);

  // Stock visually decreases the closer we are to the deadline (FOMO)
  const elapsedRatio = 1 - remaining / (4 * 60 * 60 * 1000);
  const unitsLeft = Math.max(3, Math.floor(BASE_STOCK * (1 - elapsedRatio * 0.7)));
  const progressPct = Math.max(8, 100 - Math.round((unitsLeft / BASE_STOCK) * 100));

  const fmt = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  return (
    <div
      data-testid="shipping-progress-bar"
      className="rounded-2xl border border-black/10 bg-white p-3.5 md:p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 text-[12px] font-bold text-[#0e0e10]">
        <CheckCircle2 size={15} className="text-[#22c55e]" />
        <Truck size={14} className="text-[#0e0e10]" />
        Free worldwide shipping unlocked
        <span className="ml-auto text-[10px] uppercase tracking-wider text-[#22c55e] font-extrabold">
          ✓ Yours
        </span>
      </div>

      <div className="mt-3 border-t border-dashed border-black/10 pt-3">
        <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-[#0e0e10]">
          <span className="inline-flex items-center gap-1.5">
            <Flame size={13} className="text-[#ef4444]" />
            Flash stock running low
          </span>
          <span
            data-testid="stock-countdown"
            className="font-mono text-[#ef4444]"
          >
            {fmt(remaining)}
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full bg-[#f4f4f3] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#ef4444] to-[#d8f06b] transition-[width] duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-[#6b6b72]">
          <span>
            Only <strong className="text-[#0e0e10]">{unitsLeft}</strong> left at this price
          </span>
          <span>{qty}× in your cart</span>
        </div>
      </div>
    </div>
  );
}
